import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Option } from '@interfaces/common/option';
import { PartnerDealResponse } from '@interfaces/partners/responses/partner-deal.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';

/**
 * Corrige o valor pretendido de um deal (card 2937).
 *
 * Até este card o valor era imutável: entrava uma vez, no registro, e nunca mais mudava. Um
 * erro de digitação virava número errado para sempre, e renegociar valor — que é o normal em
 * originação — não tinha para onde ir.
 *
 * **Quem vê esta modal já foi autorizado pelo servidor.** O board só oferece a ação quando
 * `deal.canEditAmount` vem verdadeiro, e esse campo é resolvido no backend pela régua
 * "usuário interno, ou master do parceiro dono". A tela não reimplementa isso — e o endpoint
 * recusa de todo modo, então esconder é conveniência, não autorização.
 */
@Component({
  selector: 'app-modal-valor-deal',
  templateUrl: './modal-valor-deal.component.html',
  styleUrls: ['./modal-valor-deal.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
})
export class ModalValorDealComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  /** Avisa o board para recarregar depois de salvar. */
  @Output() salvou = new EventEmitter<void>();

  /** Deal em edição. Exibido no cabeçalho para a pessoa conferir que é o certo. */
  deal: PartnerDealResponse | null = null;

  /** Ver o comentário homônimo na modal de vincular empresa: não use `isLoading`. */
  gravando = false;

  readonly moedas: Option[] = [
    { label: 'BRL — Real', value: 'BRL' },
    { label: 'USD — Dólar', value: 'USD' },
  ];

  form: FormGroup = this.formBuilder.group({
    amount: [null, [Validators.required, Validators.min(0.01)]],
    currencyCode: ['BRL', [Validators.required]],
  });

  constructor(
    translateService: TranslateService,
    private formBuilder: FormBuilder,
    private partnersService: PartnersHttpService,
    private toast: ToastHelperService,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);
  }

  /** Abre a modal com o valor atual do deal preenchido. */
  async abrir(deal: PartnerDealResponse): Promise<void> {
    this.deal = deal;
    this.form.reset({
      amount: deal.amount,
      currencyCode: deal.currencyCode || 'BRL',
    });

    this.cdr.markForCheck();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'valor-deal', false, true);
  }

  isFormValid(): boolean {
    return this.form.valid && !!this.deal;
  }

  submitAction(): void {
    if (!this.isFormValid() || this.gravando) return;

    const bruto = this.form.getRawValue();

    this.gravando = true;
    this.cdr.markForCheck();

    this.partnersService
      .updateDealAmount(this.deal!.id, {
        amount: Number(bruto.amount),
        currencyCode: bruto.currencyCode || 'BRL',
      })
      .subscribe({
        next: () => {
          this.gravando = false;
          this.cdr.markForCheck();
          this.toast.showToastSuccess('Valor atualizado', '');
          this.dialogRef?.close();
          this.salvou.emit();
        },
        error: error => {
          this.gravando = false;
          this.cdr.markForCheck();
          this.toast.showToastError(
            'Erro ao salvar o valor',
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }
}
